import { useState, useMemo } from 'react';
import type { Meal } from '@meal-planner/shared';
import { getAgeBracket } from '@meal-planner/shared';
import { Icon } from './ui/Icon.js';

export interface BLWAllergenEntry {
  allergen: string;
  safe: boolean;
  introducedAt?: string;
}

interface BLWCardProps {
  meal: Meal;
  familyMembers: Array<{ label?: string | null; age: number }>;
  allergenLog: BLWAllergenEntry[];
  defaultExpanded?: boolean;
}

// Seasonings/spices that should be set aside before serving an infant portion
const SEASONING_PATTERNS = [
  /\bsalt\b/i,
  /\bpepper\b/i,
  /\bchili\b/i,
  /\bchile\b/i,
  /\bcayenne\b/i,
  /\bjalape/i,
  /\bhot sauce\b/i,
  /\bsriracha\b/i,
  /\bsoy sauce\b/i,
  /\bfish sauce\b/i,
  /\btamari\b/i,
  /\bworcestershire\b/i,
  /\bgaram masala\b/i,
  /\bcurry\b/i,
  /\bbouillon\b/i,
  /\bstock cube\b/i,
];

// Top-9 allergen → ingredient name fragments
const ALLERGEN_INGREDIENT_MAP: Record<string, string[]> = {
  milk: ['milk', 'cream', 'butter', 'cheese', 'yogurt', 'whey', 'casein', 'ghee', 'lactose'],
  eggs: ['egg', 'eggs', 'mayonnaise', 'meringue'],
  peanuts: ['peanut', 'peanut butter'],
  tree_nuts: ['almond', 'walnut', 'cashew', 'pecan', 'pistachio', 'hazelnut', 'macadamia', 'pine nut'],
  wheat: ['flour', 'wheat', 'bread', 'breadcrumb', 'pasta', 'noodle', 'couscous', 'semolina', 'panko'],
  soy: ['soy', 'soy sauce', 'tofu', 'edamame', 'tempeh', 'miso'],
  sesame: ['sesame', 'tahini'],
  fish: ['salmon', 'tuna', 'cod', 'halibut', 'tilapia', 'anchovy', 'sardine', 'fish sauce'],
  shellfish: ['shrimp', 'crab', 'lobster', 'scallop', 'clam', 'oyster', 'mussel'],
};

const ALLERGEN_LABEL: Record<string, string> = {
  milk: 'Milk', eggs: 'Eggs', peanuts: 'Peanuts', tree_nuts: 'Tree nuts',
  wheat: 'Wheat', soy: 'Soy', sesame: 'Sesame', fish: 'Fish', shellfish: 'Shellfish',
};

interface UnsafeFlag {
  key: string;
  emoji: string;
  message: string;
}

function findUnsafeFlags(ingredientNames: string[]): UnsafeFlag[] {
  const flags: UnsafeFlag[] = [];
  const joined = ingredientNames.join(' | ').toLowerCase();

  if (/\bhoney\b/.test(joined)) {
    flags.push({ key: 'honey', emoji: '🍯', message: 'Honey is unsafe under 12 months (botulism risk).' });
  }
  if (/\b(whole )?(almond|peanut|walnut|cashew|pecan|pistachio|hazelnut|macadamia)\b/.test(joined)) {
    flags.push({ key: 'nuts', emoji: '🥜', message: 'Whole nuts are a choking hazard — grind or use butter.' });
  }
  if (/\b(grape|cherry tomato)s?\b/.test(joined)) {
    flags.push({ key: 'round', emoji: '⭕', message: 'Cut grapes / cherry tomatoes lengthwise into quarters.' });
  }
  if (/\bhot dog\b/.test(joined)) {
    flags.push({ key: 'hotdog', emoji: '🌭', message: 'Cut hot dogs lengthwise, then into small pieces.' });
  }
  if (/\braw (carrot|celery)\b/.test(joined) || /\b(carrot|celery) sticks?\b/.test(joined)) {
    flags.push({ key: 'rawveg', emoji: '🥕', message: 'Steam raw carrots/celery until soft for infants.' });
  }
  if (/\bpopcorn\b/.test(joined)) {
    flags.push({ key: 'popcorn', emoji: '🍿', message: 'Popcorn is not recommended for children under 4.' });
  }
  if (/\b(hard candy|jawbreaker|gumball)\b/.test(joined)) {
    flags.push({ key: 'hardcandy', emoji: '🍬', message: 'Hard candy is a choking hazard.' });
  }
  if (/\bmarshmallow\b/.test(joined)) {
    flags.push({ key: 'marsh', emoji: '☁️', message: 'Marshmallows are a choking hazard — cut small.' });
  }

  return flags;
}

type AllergenStatus = 'safe' | 'reaction' | 'untried';

interface AllergenPresence {
  allergen: string;
  label: string;
  status: AllergenStatus;
}

function scanAllergens(ingredientNames: string[], log: BLWAllergenEntry[]): AllergenPresence[] {
  const joined = ingredientNames.join(' | ').toLowerCase();
  const logByName = new Map(log.map((e) => [e.allergen, e]));

  const out: AllergenPresence[] = [];
  for (const allergen of Object.keys(ALLERGEN_INGREDIENT_MAP)) {
    const patterns = ALLERGEN_INGREDIENT_MAP[allergen];
    const present = patterns.some((p) => joined.includes(p));
    if (!present) continue;
    const entry = logByName.get(allergen);
    let status: AllergenStatus = 'untried';
    if (entry) status = entry.safe ? 'safe' : 'reaction';
    out.push({ allergen, label: ALLERGEN_LABEL[allergen] ?? allergen, status });
  }
  return out;
}

export function BLWCard({ meal, familyMembers, allergenLog, defaultExpanded = false }: BLWCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const ingredientNames = useMemo(
    () => (meal.ingredients ?? []).map((i) => i.name?.toLowerCase() ?? '').filter(Boolean),
    [meal.ingredients],
  );

  const setAsideItems = useMemo(() => {
    const seen = new Set<string>();
    const items: string[] = [];
    for (const ing of meal.ingredients ?? []) {
      const name = ing.name;
      if (!name) continue;
      if (SEASONING_PATTERNS.some((re) => re.test(name))) {
        const key = name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          items.push(name);
        }
      }
    }
    return items;
  }, [meal.ingredients]);

  const unsafeFlags = useMemo(() => findUnsafeFlags(ingredientNames), [ingredientNames]);
  const allergens = useMemo(() => scanAllergens(ingredientNames, allergenLog), [ingredientNames, allergenLog]);

  // Texture guidance keyed to the youngest infant in the roster
  const infantBracket = useMemo(() => {
    const ages = familyMembers.filter((m) => m.age <= 2).map((m) => m.age);
    if (ages.length === 0) return null;
    const youngest = Math.min(...ages);
    return getAgeBracket(youngest);
  }, [familyMembers]);

  const textureGuidance = useMemo(() => {
    if (infantBracket === 'infant') {
      return 'Offer soft, mashable pieces. Cut into finger-length strips wider than baby’s fist. Avoid round shapes.';
    }
    if (infantBracket === 'toddler') {
      return 'Small, bite-sized pieces. Soft enough to squish between fingers.';
    }
    return null;
  }, [infantBracket]);

  const contentId = `blw-content-${meal.recipeId ?? meal.name.replace(/\s+/g, '-')}`;

  return (
    <div
      role="alert"
      aria-label={`Baby Led Weaning safety guidance for ${meal.name}`}
      className="card"
      style={{
        background: 'color-mix(in oklab, var(--warning) 10%, var(--surface))',
        borderColor: 'color-mix(in oklab, var(--warning) 35%, transparent)',
        borderLeft: '4px solid var(--warning)',
        borderRadius: 'var(--r-md)',
        padding: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={contentId}
        className="flex items-center gap-3 w-full text-left"
        style={{
          minHeight: 44,
          padding: '12px 16px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <Icon name="family" size={16} style={{ color: 'var(--warning)', flexShrink: 0 }} />
        <span style={{ flex: 1, color: 'var(--warning)', fontWeight: 600, fontSize: 13 }}>
          BLW — set aside before seasoning
        </span>
        <Icon
          name="chevDown"
          size={14}
          style={{
            color: 'var(--ink-4)',
            transform: expanded ? 'rotate(180deg)' : undefined,
            transition: 'transform .2s ease',
          }}
        />
      </button>

      {expanded && (
        <div
          id={contentId}
          role="region"
          style={{
            padding: '0 16px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            borderTop: '1px solid color-mix(in oklab, var(--warning) 25%, transparent)',
            paddingTop: 12,
          }}
        >
          {setAsideItems.length > 0 && (
            <section>
              <div className="text-[10px] font-semibold uppercase mb-2" style={{ color: 'var(--ink-3)', letterSpacing: '0.16em' }}>
                Set aside these ingredients
              </div>
              <div className="flex flex-wrap gap-1.5">
                {setAsideItems.map((name) => (
                  <span
                    key={name}
                    className="chip"
                    style={{
                      background: 'color-mix(in oklab, var(--warning) 14%, transparent)',
                      color: 'var(--warning)',
                      borderColor: 'color-mix(in oklab, var(--warning) 25%, transparent)',
                      fontSize: 11.5,
                      padding: '4px 10px',
                    }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </section>
          )}

          {textureGuidance && (
            <section>
              <div className="text-[10px] font-semibold uppercase mb-1" style={{ color: 'var(--ink-3)', letterSpacing: '0.16em' }}>
                Texture guide · {infantBracket}
              </div>
              <p style={{ color: 'var(--ink-2)', fontSize: 13, margin: 0 }}>{textureGuidance}</p>
            </section>
          )}

          {unsafeFlags.length > 0 && (
            <section>
              <div className="text-[10px] font-semibold uppercase mb-2" style={{ color: 'var(--danger)', letterSpacing: '0.16em' }}>
                Unsafe for infants
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {unsafeFlags.map((f) => (
                  <li key={f.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'var(--danger)' }}>
                    <span aria-hidden="true" style={{ fontSize: 14 }}>{f.emoji}</span>
                    <span style={{ color: 'var(--ink-2)' }}>{f.message}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {allergens.length > 0 && (
            <section>
              <div className="text-[10px] font-semibold uppercase mb-2" style={{ color: 'var(--ink-3)', letterSpacing: '0.16em' }}>
                Allergens in this meal
              </div>
              <div className="flex flex-wrap gap-1.5">
                {allergens.map((a) => {
                  let bg: string, color: string, label: string;
                  if (a.status === 'safe') {
                    bg = 'color-mix(in oklab, oklch(0.74 0.14 162) 14%, transparent)';
                    color = 'oklch(0.74 0.14 162)';
                    label = `✓ ${a.label}`;
                  } else if (a.status === 'reaction') {
                    bg = 'color-mix(in oklab, var(--danger) 14%, transparent)';
                    color = 'var(--danger)';
                    label = `✗ ${a.label}`;
                  } else {
                    bg = 'var(--surface-2)';
                    color = 'var(--ink-4)';
                    label = `? ${a.label}`;
                  }
                  return (
                    <span
                      key={a.allergen}
                      className="chip"
                      style={{
                        background: bg,
                        color,
                        borderColor: 'transparent',
                        fontSize: 11.5,
                        padding: '4px 10px',
                      }}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            </section>
          )}

          <p style={{ color: 'var(--ink-4)', fontSize: 11, margin: 0 }}>
            Always consult your pediatrician before introducing new foods to infants.
          </p>
        </div>
      )}
    </div>
  );
}
